import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import ToggleCell from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/AccountList/components/ToggleCell';
import DisconnectCell from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/AccountList/components/DisconnectCell';
import EditCell from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/AccountList/components/EditCell';

interface Account {
  id: string;
  status: string;
  isSuspended: boolean;
  [key: string]: unknown;
}

interface UseAccountListColumnsParams {
  handleSuspend: (params: { id: string; isSuspended: boolean }) => void;
  handleDisconnect: (id: string) => void;
  handleOpenEditModal: (account: unknown) => void;
}

const checkDisabled = (account: Account) => account.status === 'CLOSED';

export const useAccountListColumns = ({
  handleSuspend,
  handleDisconnect,
  handleOpenEditModal,
}: UseAccountListColumnsParams) => {
  const { formatMessage } = useIntl();

  const renderSuspendCell = useCallback(
    (account: Account) => (
      <ToggleCell
        id={account.id}
        value={account.isSuspended}
        onClick={handleSuspend}
        disabled={checkDisabled(account)}
      />
    ),
    [handleSuspend],
  );

  const renderDisconnectCell = useCallback(
    (account: Account) => (
      <DisconnectCell id={account.id} onClick={handleDisconnect} disabled={checkDisabled(account)} />
    ),
    [handleDisconnect],
  );

  const renderEditCell = useCallback(
    (account: Account) => <EditCell value={account} onClick={handleOpenEditModal} disabled={checkDisabled(account)} />,
    [handleOpenEditModal],
  );

  return useMemo(
    () => [
      {
        id: 'name',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.accountName',
          defaultMessage: 'Account Name',
        }),
        searchable: true,
      },
      {
        id: 'accountNumber',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.accountNumber',
          defaultMessage: 'Account Number',
        }),
        searchable: true,
      },
      {
        id: 'bank',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.bank',
          defaultMessage: 'Bank',
        }),
        searchable: true,
      },
      {
        id: 'psuId',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.psuId',
          defaultMessage: 'Authorized Signatory ID Number',
        }),
        searchable: true,
      },
      {
        id: 'status',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.status',
          defaultMessage: 'Status',
        }),
        searchable: true,
      },
      {
        id: 'isSuspend',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.suspendAccount',
          defaultMessage: 'Suspend Account',
        }),
        component: renderSuspendCell,
        searchable: false,
      },
      {
        id: 'delete',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.deleteAccount',
          defaultMessage: 'Delete Account',
        }),
        component: renderDisconnectCell,
        searchable: false,
      },
      {
        id: 'edit',
        label: formatMessage({
          id: 'configurations.company.paymentProviders.detailsModal.accountList.tableHeader.editAccount',
          defaultMessage: 'Edit Account',
        }),
        component: renderEditCell,
        searchable: false,
      },
    ],
    [formatMessage, renderSuspendCell, renderDisconnectCell, renderEditCell],
  );
};
